import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';

export interface PublishedProcedureVersionUpdateBannerProps {
  procedureType: ProcedureType;
  onApplyUpdate: () => void;
  isApplying?: boolean;
}

/**
 * Banner displayed when a procedure run plan's procedure has a newer published version available.
 * Blocks the user from creating the procedure run and provides an action button to apply the update.
 */
const PROCEDURE_TYPE_TO_RUN_STRING: Record<ProcedureType, string> = {
  [ProcedureType.ASSAY]: 'experiment',
  [ProcedureType.RECIPE]: 'batch',
};

export default function PublishedProcedureVersionUpdateBanner({
  procedureType,
  onApplyUpdate,
  isApplying = false,
}: PublishedProcedureVersionUpdateBannerProps): JSX.Element {
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const procedureRunString = PROCEDURE_TYPE_TO_RUN_STRING[procedureType];

  return (
    <div
      className={css(styles.bannerContainer)}
      {...getTestProps({component: 'PublishedProcedureVersionUpdateBanner', element: 'root'})}
    >
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text={`A new version of this ${procedureString} has been published.`}
        supportingInfo={`You must update to the latest version before creating the ${procedureRunString}. Your existing inputs will be preserved if the ${procedureString} structure is unchanged.`}
        action={{
          actionType: InlineBannerActionType.CUSTOM,
          actionText: isApplying ? 'Applying...' : 'Apply update',
          actionOnClick: onApplyUpdate,
          buttonStyle: ButtonStyle.SECONDARY,
          rightAlign: true,
        }}
        dataTestAttrs={{component: 'PublishedProcedureVersionUpdateBanner', element: 'banner'}}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  bannerContainer: {
    padding: '12px 12px 0 12px',
  },
});
